"use client";

import React, { useEffect, useState } from "react";
import { ShieldCheck, Lock, Server, RefreshCw, CircleCheck, TriangleAlert, Activity } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function CybersecurityBusinessContinuity() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  const [scanProgress, setScanProgress] = useState(0);
  useEffect(() => {
    if (isVisible) {
      const interval = setInterval(() => {
        setScanProgress(prev => {
          if (prev >= 100) {
            clearInterval(interval);
            return 100;
          }
          return prev + 1;
        });
      }, 20);
      return () => clearInterval(interval);
    }
  }, [isVisible]);
  const features = [{
    title: "Protezione Endpoint",
    description: "Gestione e protezione dei dispositivi aziendali per garantire sicurezza operativa e controllo degli endpoint utilizzati dagli utenti.",
    icon: ShieldCheck
  }, {
    title: "Backup & Disaster Recovery",
    description: "Strategie di backup immutabili e piani di ripristino rapido per garantire la continuità operativa.",
    icon: RefreshCw
  }, {
    title: "Analisi Vulnerabilità",
    description: "Monitoraggio costante e patching dei sistemi per prevenire sfruttamenti di sicurezza.",
    icon: Activity
  }];
  return <section id="cybersecurity-business-continuity" ref={ref} className="dark bg-background text-foreground py-20 md:py-32 relative overflow-hidden">
      {}
      <div className="absolute top-1/2 left-0 -translate-y-1/2 w-1/2 h-full bg-chart-2/5 blur-[120px] pointer-events-none" />

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          
          {}
          <div className={`transition-all duration-1000 delay-200 ${isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-10"}`}>
            <Card className="bg-card/40 backdrop-blur-md border-chart-2/20 shadow-2xl overflow-hidden relative">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-chart-2 to-transparent opacity-50" />
              
              <CardHeader className="pb-2">
                <div className="flex justify-between items-center">
                  <CardTitle className="text-lg font-mono tracking-tight flex items-center gap-2">
                    <ShieldCheck className="w-5 h-5 text-chart-2" />
                    SYSTEM_HEALTH_CHECK
                  </CardTitle>
                  <span className="text-xs font-mono text-chart-2 animate-pulse">LIVE MONITORING</span>
                </div>
                <CardDescription className="text-xs font-mono">
                  Analisi in tempo reale delle infrastrutture critiche
                </CardDescription>
              </CardHeader>

              <CardContent className="space-y-8 pt-6">
                {}
                <div className="flex justify-center py-4">
                  <div className="relative w-48 h-48 flex items-center justify-center">
                    {}
                    <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
                      <circle cx="50" cy="50" r="45" fill="none" stroke="currentColor" strokeWidth="2" className="text-muted/20" />
                      <circle cx="50" cy="50" r="45" fill="none" stroke="currentColor" strokeWidth="2" strokeDasharray="283" strokeDashoffset={283 - 283 * scanProgress / 100} className="text-chart-2 transition-all duration-300 ease-out" />
                    </svg>
                    
                    {}
                    <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
                      <ShieldCheck className={`w-12 h-12 mb-2 transition-all duration-500 ${scanProgress === 100 ? "text-chart-2 scale-110" : "text-muted-foreground"}`} />
                      <div className="text-2xl font-bold font-mono">{scanProgress}%</div>
                      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                        {scanProgress < 100 ? "Scanning..." : "Secure"}
                      </div>
                    </div>
                  </div>
                </div>

                {}
                <div className="space-y-4">
                  <div className="space-y-1">
                    <div className="flex justify-between text-xs font-medium">
                      <span className="flex items-center gap-1.5">
                        <Lock className="w-3 h-3 text-chart-2" /> Endpoint Security
                      </span>
                      <span className="text-chart-2">Ottimale</span>
                    </div>
                    <Progress value={scanProgress > 20 ? 100 : scanProgress * 5} className="h-1.5 bg-muted/30 [&>[data-slot=progress-indicator]]:bg-chart-2" />
                  </div>

                  <div className="space-y-1">
                    <div className="flex justify-between text-xs font-medium">
                      <span className="flex items-center gap-1.5">
                        <Server className="w-3 h-3 text-chart-2" /> Backup Integrity
                      </span>
                      <span className="text-chart-2">Verificato</span>
                    </div>
                    <Progress value={scanProgress > 40 ? 100 : Math.max(0, (scanProgress - 20) * 5)} className="h-1.5 bg-muted/30 [&>[data-slot=progress-indicator]]:bg-chart-2" />
                  </div>

                  <div className="space-y-1">
                    <div className="flex justify-between text-xs font-medium">
                      <span className="flex items-center gap-1.5">
                        <TriangleAlert className="w-3 h-3 text-chart-2" /> Threat Detection
                      </span>
                      <span className="text-chart-2">Attivo</span>
                    </div>
                    <Progress value={scanProgress > 60 ? 100 : Math.max(0, (scanProgress - 40) * 5)} className="h-1.5 bg-muted/30 [&>[data-slot=progress-indicator]]:bg-chart-2" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {}
          <div className={`space-y-8 transition-all duration-1000 delay-400 ${isVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-10"}`}>
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4 leading-tight">
                Cybersecurity & <br />
                <span className="text-chart-2">Business Continuity</span>
              </h2>
              <p className="text-muted-foreground text-lg leading-relaxed">La protezione dei sistemi aziendali è fondamentale per garantire continuità operativa e affidabilità dei servizi. B-Tech supporta le aziende nell’implementazione di strategie di protezione dei dati, sicurezza degli endpoint e gestione degli accessi, riducendo i rischi operativi.</p>
            </div>

            <div className="space-y-6">
              {features.map((feature, i) => <div key={i} data-index={i} className="flex gap-4 group">
                  <div className="mt-1">
                    <div className="w-10 h-10 rounded-lg bg-chart-2/10 flex items-center justify-center group-hover:bg-chart-2/20 transition-colors">
                      <feature.icon className="w-5 h-5 text-chart-2" />
                    </div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg text-foreground group-hover:text-chart-2 transition-colors">
                      {feature.title}
                    </h3>
                    <p className="text-muted-foreground text-sm mt-1">
                      {feature.description}
                    </p>
                  </div>
                </div>)}
            </div>

            <div className="pt-4 flex flex-col sm:flex-row gap-4">
              <Button asChild size="lg" className="bg-chart-2 hover:bg-chart-2/90 text-white border-none shadow-[0_0_20px_-5px_hsl(160,84%,39%)]">
                
              </Button>
              <Button asChild variant="outline" size="lg" className="border-chart-2/30 hover:bg-chart-2/10 hover:text-chart-2">
                
              </Button>
            </div>
          </div>

        </div>
      </div>
    </section>;
}